/**
 * New Chat store module (namespaced: true).
 * Access: this.$store.dispatch('newChat/setTab', 'common')
 *         this.$store.getters['newChat/activeTab']
 */
export default {
    namespaced: true,
    state: {
        tab: 'common',       // 'common' | 'personal' | 'history'
        source: 'regular',   // 'regular' | 'ia' | 'all'
        isLoading: false,
        list: [],
        activeChat: null,
        reference: null,
        isDetailLoading: false,
        messages: [],
        messagesRevision: 0,
        isMessagesLoading: false,
        fcmPermission: null, // null | 'default' | 'granted' | 'denied'
        soundEnabled: localStorage.getItem('nc_sound_enabled') !== 'false',
        totalUnread: 0,
        unreadMyChats: 0,
        quickRepliesOpen: false,
        quickReplyPending: null,
        historySearchPrefill: '',
    },
    mutations: {
        SET_TAB(state, tab) {
            state.tab = tab
        },
        SET_SOURCE(state, source) {
            state.source = source
        },
        SET_LOADING(state, value) {
            state.isLoading = value
        },
        SET_LIST(state, list) {
            state.list = list
        },
        SET_ACTIVE_CHAT(state, chat) {
            state.activeChat = chat
        },
        SET_REFERENCE(state, reference) {
            state.reference = reference
        },
        SET_DETAIL_LOADING(state, value) {
            state.isDetailLoading = value
        },
        SET_MESSAGES(state, messages) {
            state.messages = messages
            state.messagesRevision++
        },
        PREPEND_MESSAGES(state, messages) {
            const existingIds = new Set(state.messages.map(m => m.id))
            const unique = messages.filter(m => !existingIds.has(m.id))
            state.messages = [...unique, ...state.messages]
        },
        SET_MESSAGES_LOADING(state, value) {
            state.isMessagesLoading = value
        },
        APPEND_MESSAGE(state, message) {
            const exists = state.messages.some(m => m.id === message.id)
            if (!exists) state.messages.push(message)
        },
        UPDATE_MESSAGE(state, { id, patch }) {
            const idx = state.messages.findIndex(m => m.id === id)
            if (idx !== -1) {
                state.messages.splice(idx, 1, { ...state.messages[idx], ...patch })
            }
        },
        REMOVE_MESSAGE_BY_ID(state, id) {
            const idx = state.messages.findIndex(m => m.id === id)
            if (idx !== -1) state.messages.splice(idx, 1)
        },
        SET_FCM_PERMISSION(state, value) {
            state.fcmPermission = value
        },
        SET_TOTAL_UNREAD(state, value) {
            state.totalUnread = value
        },
        SET_UNREAD_MY_CHATS(state, value) {
            state.unreadMyChats = value
        },
        SET_SOUND_ENABLED(state, value) {
            state.soundEnabled = value
            localStorage.setItem('nc_sound_enabled', value)
        },
        SET_QUICK_REPLIES_OPEN(state, value) {
            state.quickRepliesOpen = value
        },
        SET_QUICK_REPLY_PENDING(state, phrase) {
            state.quickReplyPending = phrase
        },
        SET_HISTORY_SEARCH_PREFILL(state, value) {
            state.historySearchPrefill = value
        },
    },
    actions: {
        setTab({ commit }, tab) {
            commit('SET_TAB', tab)
        },
        setSource({ commit }, source) {
            commit('SET_SOURCE', source)
        },
        setLoading({ commit }, value) {
            commit('SET_LOADING', value)
        },
        setList({ commit }, list) {
            commit('SET_LIST', list)
        },
        setActiveChat({ commit }, chat) {
            commit('SET_ACTIVE_CHAT', chat)
        },
        setReference({ commit }, reference) {
            commit('SET_REFERENCE', reference)
        },
        setDetailLoading({ commit }, value) {
            commit('SET_DETAIL_LOADING', value)
        },
        setMessages({ commit }, messages) {
            commit('SET_MESSAGES', messages)
        },
        prependMessages({ commit }, messages) {
            commit('PREPEND_MESSAGES', messages)
        },
        setMessagesLoading({ commit }, value) {
            commit('SET_MESSAGES_LOADING', value)
        },
        appendMessage({ commit }, message) {
            commit('APPEND_MESSAGE', message)
        },
        updateMessage({ commit }, payload) {
            commit('UPDATE_MESSAGE', payload)
        },
        removeMessageById({ commit }, id) {
            commit('REMOVE_MESSAGE_BY_ID', id)
        },
        setFcmPermission({ commit }, value) {
            commit('SET_FCM_PERMISSION', value)
        },
        setTotalUnread({ commit }, value) {
            commit('SET_TOTAL_UNREAD', value)
        },
        setUnreadMyChats({ commit }, value) {
            commit('SET_UNREAD_MY_CHATS', value)
        },
        setSoundEnabled({ commit }, value) {
            commit('SET_SOUND_ENABLED', value)
        },
        setQuickRepliesOpen({ commit }, value) {
            commit('SET_QUICK_REPLIES_OPEN', value)
        },
        applyQuickReply({ commit }, phrase) {
            commit('SET_QUICK_REPLY_PENDING', phrase)
        },
        clearQuickReply({ commit }) {
            commit('SET_QUICK_REPLY_PENDING', null)
        },
        setHistorySearchPrefill({ commit }, value) {
            commit('SET_HISTORY_SEARCH_PREFILL', value)
        },
    },
    getters: {
        activeTab: state => state.tab,
        activeSource: state => state.source,
        isLoading: state => state.isLoading,
        list: state => state.list,
        activeChat: state => state.activeChat,
        reference: state => state.reference,
        isDetailLoading: state => state.isDetailLoading,
        messages: state => state.messages,
        messagesRevision: state => state.messagesRevision,
        isMessagesLoading: state => state.isMessagesLoading,
        fcmPermission: state => state.fcmPermission,
        totalUnread: state => state.totalUnread,
        unreadMyChats: state => state.unreadMyChats,
        soundEnabled: state => state.soundEnabled,
        quickRepliesOpen: state => state.quickRepliesOpen,
        quickReplyPending: state => state.quickReplyPending,
        historySearchPrefill: state => state.historySearchPrefill,
    }
}
